import React, { useMemo, useState } from 'react';
import { ExternalLink, Lock, X, Check, Plus, ToggleRight, ToggleLeft, ChevronRight, ChevronUp, ChevronDown, Trash2, Globe, ShieldCheck, AlertCircle, Clock, CheckCircle, FileCheck, GraduationCap, FileText, Upload, Save, Lightbulb, AlertTriangle, Search, Camera, Eye, Monitor, Smartphone, Star, MessageSquare, Calendar, Video, Mic, BarChart3, Pin, EyeOff, ArrowRight, Sparkles, MapPin, ShoppingBag } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface ProfileTabProps {
  showToast: (msg: string) => void;
  onNavigate: (page: string) => void;
}
interface ProfileBlock {
  id: string;
  label: string;
  enabled: boolean;
  location: string;
}
const ALL_SPECIALIZATIONS = ['Западная астрология', 'Натальная карта', 'Прогностика', 'Синастрия', 'Ведическая астрология', 'Хорарная астрология', 'Элективная астрология', 'Кармическая астрология', 'Медицинская астрология', 'Финансовая астрология', 'Детская астрология', 'Астропсихология', 'Нумерология', 'Таро', 'Астрокартография', 'Лунная астрология', 'Бизнес-астрология'];
const RECOMMENDED_ORDER = ['about', 'services', 'reviews', 'products', 'education', 'faq'];
const MAX_ABOUT_LENGTH = 1000;
export function ProfileTab({
  showToast,
  onNavigate
}: ProfileTabProps) {
  // Preview mode
  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [highlightedBlock, setHighlightedBlock] = useState<string | null>(null);
  // Main info
  const [firstName, setFirstName] = useState('Елена');
  const [lastName, setLastName] = useState('Смирнова');
  const [experience, setExperience] = useState('8');
  const [languages, setLanguages] = useState('Русский, Английский');
  const [aboutText, setAboutText] = useState('Привет! Я Елена, профессиональный астролог с 8-летним стажем. Моя цель — перевести сложный язык звёзд на понятный человеческий. В своей работе я использую методы классической западной астрологии, подкрепленные знаниями в области психологии.');
  const [showAboutHint, setShowAboutHint] = useState(false);
  // Specializations
  const [specializations, setSpecializations] = useState(['Западная астрология', 'Натальная карта', 'Прогностика', 'Синастрия']);
  const [specSearch, setSpecSearch] = useState('');
  const [showSpecDropdown, setShowSpecDropdown] = useState(false);
  const [customSpecInput, setCustomSpecInput] = useState('');
  const [showCustomSpecInput, setShowCustomSpecInput] = useState(false);
  // Contacts
  const [contacts, setContacts] = useState([{
    id: 'tg',
    label: 'Telegram',
    code: 'TG',
    color: 'bg-sky-100 text-sky-600',
    value: '@elena_astro',
    enabled: true
  }, {
    id: 'wa',
    label: 'WhatsApp',
    code: 'WA',
    color: 'bg-emerald-100 text-emerald-600',
    value: '+7 999 123-45-67',
    enabled: true
  }, {
    id: 'ig',
    label: 'Instagram',
    code: 'IG',
    color: 'bg-pink-100 text-pink-600',
    value: 'elena_stars',
    enabled: false
  }, {
    id: 'web',
    label: 'Сайт',
    code: '🌐',
    color: 'bg-slate-100 text-slate-600',
    value: 'https://elena-astro.ru',
    enabled: true
  }, {
    id: 'email',
    label: 'Email',
    code: '@',
    color: 'bg-blue-100 text-blue-600',
    value: 'elena@astro.ru',
    enabled: true
  }]);
  const [showChatButton, setShowChatButton] = useState(true);
  const [showBookButton, setShowBookButton] = useState(true);
  // Consultation settings
  const [consultPrice, setConsultPrice] = useState('5000');
  const [consultPriceType, setConsultPriceType] = useState<'fixed' | 'from'>('from');
  const [consultDuration, setConsultDuration] = useState('60');
  const [consultFormats, setConsultFormats] = useState({
    video: true,
    chat: true,
    audio: false
  });
  const [consultButtonText, setConsultButtonText] = useState('Записаться на консультацию');
  // Services
  const [services, setServices] = useState([{
    id: 1,
    name: 'Разбор натальной карты (Видео)',
    price: '5 000 ₽',
    duration: '60 мин',
    isTop: true
  }, {
    id: 2,
    name: 'Соляр на год (Чат)',
    price: '4 000 ₽',
    duration: '3 дня',
    isTop: false
  }, {
    id: 3,
    name: 'Синастрия',
    price: '6 000 ₽',
    duration: '90 мин',
    isTop: false
  }]);
  // Stats block
  const [showStats, setShowStats] = useState(true);
  const statsData = {
    consultations: 1250,
    rating: 94,
    responseTime: 15
  };
  // Reviews settings
  const [reviewsMode, setReviewsMode] = useState<'all' | 'best'>('best');
  const [pinnedReviews] = useState([1, 3]);
  // Products settings
  const [showProducts, setShowProducts] = useState(true);
  const [productsCount, setProductsCount] = useState<3 | 6 | 'all'>(3);
  // Profile blocks (constructor)
  const [blocks, setBlocks] = useState<ProfileBlock[]>([{
    id: 'about',
    label: 'Обо мне',
    enabled: true,
    location: 'Первый текстовый блок под шапкой'
  }, {
    id: 'services',
    label: 'Услуги',
    enabled: true,
    location: 'Блок «Услуги» на странице'
  }, {
    id: 'products',
    label: 'Товары / Курсы',
    enabled: false,
    location: 'Блок «Товары» после услуг'
  }, {
    id: 'reviews',
    label: 'Отзывы',
    enabled: true,
    location: 'Блок «Отзывы клиентов»'
  }, {
    id: 'education',
    label: 'Образование',
    enabled: true,
    location: 'Блок «Образование и сертификаты»'
  }, {
    id: 'faq',
    label: 'FAQ',
    enabled: true,
    location: 'Блок «Частые вопросы» внизу'
  }]);
  // Verification
  const [verificationStatus, setVerificationStatus] = useState<'unverified' | 'pending' | 'verified'>('unverified');
  // Collapsed sections
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    contacts: true,
    services: false,
    consultation: true,
    constructor: true,
    stats: true,
    reviews: true,
    products: true,
    verification: true
  });
  const toggle = (s: string) => setCollapsed(p => ({
    ...p,
    [s]: !p[s]
  }));
  // Progress
  const progress = useMemo(() => {
    let score = 0,
      total = 8;
    if (firstName.trim()) score++;
    if (lastName.trim()) score++;
    if (aboutText.trim().length > 50) score++;
    if (specializations.length >= 2) score++;
    if (contacts.some(c => c.enabled && c.value.trim())) score++;
    if (services.length > 0) score++;
    if (experience.trim()) score++;
    if (consultPrice.trim()) score++;
    return Math.round(score / total * 100);
  }, [firstName, lastName, aboutText, specializations, contacts, services, experience, consultPrice]);
  const filteredSpecs = ALL_SPECIALIZATIONS.filter(s => !specializations.includes(s) && s.toLowerCase().includes(specSearch.toLowerCase()));
  const moveBlock = (idx: number, dir: 'up' | 'down') => {
    setBlocks(prev => {
      const next = [...prev];
      const t = dir === 'up' ? idx - 1 : idx + 1;
      if (t < 0 || t >= next.length) return prev;
      [next[idx], next[t]] = [next[t], next[idx]];
      return next;
    });
  };
  const applyRecommendedOrder = () => {
    setBlocks(prev => {
      const sorted = [...prev].sort((a, b) => {
        const ai = RECOMMENDED_ORDER.indexOf(a.id);
        const bi = RECOMMENDED_ORDER.indexOf(b.id);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
      return sorted;
    });
    showToast('Рекомендуемый порядок применён');
  };
  const tips = useMemo(() => {
    const t: {
      text: string;
      action: string;
    }[] = [];
    if (aboutText.trim().length < 100) t.push({
      text: 'Добавьте описание (мин. 100 символов)',
      action: 'Написать'
    });
    if (specializations.length < 3) t.push({
      text: 'Добавьте больше специализаций',
      action: 'Добавить'
    });
    if (services.length < 3) t.push({
      text: 'Астрологи с 3+ услугами получают на 30% больше заказов',
      action: 'Добавить'
    });
    if (verificationStatus === 'unverified') t.push({
      text: 'Верификация повышает доверие клиентов',
      action: 'Пройти'
    });
    return t;
  }, [aboutText, specializations, services, verificationStatus]);
  const handleSave = () => {
    const errors: string[] = [];
    if (!firstName.trim() || !lastName.trim()) errors.push('Укажите имя и фамилию');
    if (aboutText.trim().length < 30) errors.push('Описание слишком короткое');
    if (services.length === 0) errors.push('Добавьте хотя бы 1 услугу');
    if (errors.length > 0) {
      showToast(`⚠️ ${errors[0]}`);
      return;
    }
    showToast('Изменения сохранены ✓');
    console.log('[Admin Sync] Profile updated');
  };
  const addCustomSpec = () => {
    const val = customSpecInput.trim();
    if (val && !specializations.includes(val)) {
      setSpecializations(p => [...p, val]);
      setCustomSpecInput('');
      setShowCustomSpecInput(false);
      showToast('Специализация добавлена');
    }
  };
  // Section header helper
  const SectionHeader = ({
    title,
    section,
    badge
  }: {
    title: string;
    section: string;
    badge?: React.ReactNode;
  }) => <div className="flex justify-between items-center cursor-pointer" onClick={() => toggle(section)} data-id="element-13827">
      <h3 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-13828">{title}</h3>
      <div className="flex items-center gap-2" data-id="element-13829">
        {badge}
        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${!collapsed[section] ? 'rotate-90' : ''}`} data-id="element-13830" />
      </div>
    </div>;
  const SectionBody = ({
    section,
    children
  }: {
    section: string;
    children: React.ReactNode;
  }) => <div className={`overflow-hidden transition-all duration-300 ${!collapsed[section] ? 'max-h-[5000px] mt-5 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-13831">
      {children}
    </div>;
  // Mini preview component
  const MiniPreview = () => <div className={`bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm ${previewDevice === 'mobile' ? 'max-w-[320px] mx-auto' : 'w-full'}`} data-id="element-13832">
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 text-white" data-id="element-13833">
        <div className="flex items-center gap-3" data-id="element-13834">
          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold" data-id="element-13835">
            {firstName.charAt(0)}
            {lastName.charAt(0)}
          </div>
          <div data-id="element-13836">
            <div className="font-bold text-lg" data-id="element-13837">
              {firstName} {lastName}
            </div>
            <div className="text-indigo-100 text-xs" data-id="element-13838">
              {specializations.slice(0, 2).join(' · ')}
            </div>
            <div className="flex items-center gap-2 mt-1" data-id="element-13839">
              <span className="text-xs" data-id="element-13840">⭐ 4.9</span>
              <span className="text-xs text-indigo-200" data-id="element-13841">
                · {experience} лет опыта
              </span>
            </div>
          </div>
        </div>
      </div>

      {showStats && <div className={`grid grid-cols-3 gap-2 p-3 border-b border-slate-100 ${highlightedBlock === 'stats' ? 'ring-2 ring-indigo-400 bg-indigo-50' : ''}`} data-id="element-13842">
          <div className="text-center" data-id="element-13843">
            <div className="font-bold text-sm text-slate-900" data-id="element-13844">
              {statsData.consultations}+
            </div>
            <div className="text-[10px] text-slate-500" data-id="element-13845">консультаций</div>
          </div>
          <div className="text-center" data-id="element-13846">
            <div className="font-bold text-sm text-slate-900" data-id="element-13847">
              {statsData.rating}%
            </div>
            <div className="text-[10px] text-slate-500" data-id="element-13848">довольны</div>
          </div>
          <div className="text-center" data-id="element-13849">
            <div className="font-bold text-sm text-slate-900" data-id="element-13850">
              {statsData.responseTime} мин
            </div>
            <div className="text-[10px] text-slate-500" data-id="element-13851">ответ</div>
          </div>
        </div>}

      <div className="p-3 space-y-3" data-id="element-13852">
        {blocks.filter(b => b.enabled).map(block => <div key={block.id} className={`p-2 rounded-lg border text-xs transition-all ${highlightedBlock === block.id ? 'border-indigo-400 bg-indigo-50 ring-1 ring-indigo-400' : 'border-slate-100'}`} data-id="element-13853">
              <div className="font-bold text-slate-700 mb-0.5" data-id="element-13854">
                {block.label}
              </div>
              {block.id === 'about' && <div className="text-slate-500 line-clamp-2" data-id="element-13855">
                  {aboutText.slice(0, 80)}...
                </div>}
              {block.id === 'services' && <div className="text-slate-500" data-id="element-13856">
                  {services.length} услуг · от{' '}
                  {consultPriceType === 'from' ? '' : ''}
                  {consultPrice} ₽
                </div>}
              {block.id === 'reviews' && <div className="text-slate-500" data-id="element-13857">⭐ 4.9 · 124 отзыва</div>}
              {block.id === 'products' && <div className="text-slate-500" data-id="element-13858">Курсы и товары</div>}
              {block.id === 'education' && <div className="text-slate-500" data-id="element-13859">Образование и сертификаты</div>}
              {block.id === 'faq' && <div className="text-slate-500" data-id="element-13860">Частые вопросы</div>}
            </div>)}
      </div>

      <div className="p-3 border-t border-slate-100 space-y-2" data-id="element-13861">
        {showBookButton && <div className="w-full py-2 bg-indigo-600 text-white text-center rounded-lg text-xs font-bold" data-id="element-13862">
            {consultButtonText}
          </div>}
        {showChatButton && <div className="w-full py-2 bg-slate-100 text-slate-700 text-center rounded-lg text-xs font-medium" data-id="element-13863">
            Написать в чат
          </div>}
      </div>
    </div>;
  return <div className="space-y-5 pb-20 sm:pb-6" data-id="element-13864">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-13865">
        <div data-id="element-13866">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-13867">
            Редактирование профиля
          </h2>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-13868">
            Что редактируете → сразу видите на странице клиента
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto" data-id="element-13869">
          <Button variant={showPreview ? 'primary' : 'outline'} size="sm" icon={<Eye className="w-4 h-4" data-id="element-13871" />} onClick={() => setShowPreview(!showPreview)} className="flex-1 sm:flex-none" data-id="element-13870">
            {showPreview ? 'Скрыть превью' : 'Превью'}
          </Button>
          <Button variant="outline" size="sm" icon={<ExternalLink className="w-4 h-4" data-id="element-13873" />} onClick={() => onNavigate('astrologer-profile')} className="flex-1 sm:flex-none" data-id="element-13872">
            Открыть страницу
          </Button>
          <Button variant="primary" size="sm" icon={<Save className="w-4 h-4" data-id="element-13875" />} onClick={handleSave} className="flex-1 sm:flex-none" data-id="element-13874">
            Сохранить
          </Button>
        </div>
      </div>

      {/* Progress */}
      <Card padding="sm" className={progress === 100 ? 'border-emerald-200 bg-emerald-50/30' : 'border-amber-200 bg-amber-50/30'} data-id="element-13876">
        <div className="flex items-center justify-between mb-1.5" data-id="element-13877">
          <span className="text-sm font-bold text-slate-900" data-id="element-13878">
            Заполнение профиля
          </span>
          <span className={`text-sm font-bold ${progress === 100 ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-13879">
            {progress}%
          </span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-13880">
          <div className={`h-2 rounded-full transition-all duration-500 ${progress === 100 ? 'bg-emerald-500' : progress >= 70 ? 'bg-amber-400' : 'bg-rose-400'}`} style={{
          width: `${progress}%`
        }} data-id="element-13881" />
        </div>
        {progress < 100 && <p className="text-xs text-amber-700 mt-1.5" data-id="element-13882">
            Заполните полностью → до +40% заказов
          </p>}
      </Card>

      {/* Tips */}
      {tips.length > 0 && <Card padding="sm" data-id="element-13883">
          <div className="flex items-center gap-2 mb-2" data-id="element-13884">
            <Lightbulb className="w-4 h-4 text-amber-500" data-id="element-13885" />
            <span className="text-sm font-bold text-slate-900" data-id="element-13886">
              Как улучшить профиль
            </span>
          </div>
          <div className="space-y-1.5" data-id="element-13887">
            {tips.slice(0, 3).map((tip, i) => <div key={i} className="flex items-center justify-between gap-3 text-sm" data-id="element-13888">
                <span className="text-slate-600 flex-1" data-id="element-13889">{tip.text}</span>
                <button className="text-indigo-600 font-medium text-xs hover:text-indigo-700 shrink-0" data-id="element-13890">
                  {tip.action} →
                </button>
              </div>)}
          </div>
        </Card>}

      {/* Main layout: Editor + Preview */}
      <div className={`flex gap-6 ${showPreview ? 'flex-col lg:flex-row' : ''}`} data-id="element-13891">
        {/* Editor Column */}
        <div className={`space-y-5 ${showPreview ? 'flex-1 min-w-0' : 'w-full'}`} data-id="element-13892">
          {/* Main Info */}
          <Card padding="md" data-id="element-13893">
            <div className="flex justify-between items-center mb-4" data-id="element-13894">
              <h3 className="font-bold text-slate-900" data-id="element-13895">Основная информация</h3>
              <Badge variant="neutral" className="bg-slate-100 text-[10px]" data-id="element-13896">
                <Lock className="w-3 h-3 mr-1" data-id="element-13897" /> Обязательно
              </Badge>
            </div>
            <p className="text-[11px] text-indigo-600 mb-4 flex items-center gap-1" data-id="element-13898">
              <MapPin className="w-3 h-3" data-id="element-13899" /> Отображается: верх профиля (имя,
              рейтинг, опыт)
            </p>

            <div className="flex flex-col md:flex-row gap-5 mb-5" data-id="element-13900">
              <div className="flex flex-col items-center gap-2 w-28 shrink-0" data-id="element-13901">
                <div className="relative w-28 h-28 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden group" data-id="element-13902">
                  <div className="w-full h-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center" data-id="element-13903">
                    <span className="text-3xl font-bold text-indigo-300" data-id="element-13904">
                      {firstName.charAt(0)}
                      {lastName.charAt(0)}
                    </span>
                  </div>
                  <button onClick={() => showToast('Загрузка фото...')} className="absolute inset-0 bg-slate-900/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-13905">
                    <Camera className="w-5 h-5 text-white" data-id="element-13906" />
                  </button>
                </div>
                <button onClick={() => showToast('Выбор фото...')} className="text-xs text-indigo-600 font-medium" data-id="element-13907">
                  Заменить
                </button>
              </div>
              <div className="flex-1 space-y-3" data-id="element-13908">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-13909">
                  <div data-id="element-13910">
                    <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-13911">
                      Имя
                    </label>
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" data-id="element-13912" />
                  </div>
                  <div data-id="element-13913">
                    <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-13914">
                      Фамилия
                    </label>
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" data-id="element-13915" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-13916">
                  <div data-id="element-13917">
                    <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-13918">
                      Опыт (лет)
                    </label>
                    <input type="number" value={experience} onChange={e => setExperience(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" data-id="element-13919" />
                  </div>
                  <div data-id="element-13920">
                    <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-13921">
                      Языки
                    </label>
                    <input type="text" value={languages} onChange={e => setLanguages(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" data-id="element-13922" />
                  </div>
                </div>
              </div>
            </div>

            {/* Specializations */}
            <div className="mb-5" data-id="element-13923">
              <label className="block text-xs font-medium text-slate-600 mb-2" data-id="element-13924">
                Специализации
              </label>
              <div className="flex flex-wrap gap-1.5 mb-2" data-id="element-13925">
                {specializations.map(tag => <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium border border-indigo-100" data-id="element-13926">
                    {tag}{' '}
                    <X className="w-3 h-3 cursor-pointer hover:text-rose-500" onClick={() => setSpecializations(p => p.filter(s => s !== tag))} data-id="element-13927" />
                  </span>)}
              </div>
              <div className="flex gap-2" data-id="element-13928">
                <div className="relative flex-1" data-id="element-13929">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-13930" />
                  <input type="text" value={specSearch} onChange={e => {
                  setSpecSearch(e.target.value);
                  setShowSpecDropdown(true);
                }} onFocus={() => setShowSpecDropdown(true)} placeholder="Поиск..." className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-13931" />
                  {showSpecDropdown && filteredSpecs.length > 0 && <div className="absolute z-10 top-full mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-36 overflow-y-auto" data-id="element-13932">
                      {filteredSpecs.map(s => <button key={s} onClick={() => {
                    setSpecializations(p => [...p, s]);
                    setSpecSearch('');
                    setShowSpecDropdown(false);
                  }} className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 hover:text-indigo-700" data-id="element-13933">
                          {s}
                        </button>)}
                    </div>}
                </div>
                {showCustomSpecInput ? <div className="flex items-center gap-1" data-id="element-13934">
                    <input type="text" value={customSpecInput} onChange={e => setCustomSpecInput(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') addCustomSpec();
                  if (e.key === 'Escape') setShowCustomSpecInput(false);
                }} placeholder="Своя..." className="w-32 rounded-lg border-slate-200 px-3 py-2 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none" autoFocus data-id="element-13935" />
                    <button onClick={addCustomSpec} className="p-1.5 text-emerald-500 hover:text-emerald-700" data-id="element-13936">
                      <Check className="w-4 h-4" data-id="element-13937" />
                    </button>
                    <button onClick={() => {
                  setShowCustomSpecInput(false);
                  setCustomSpecInput('');
                }} className="p-1.5 text-slate-400 hover:text-slate-600" data-id="element-13938">
                      <X className="w-4 h-4" data-id="element-13939" />
                    </button>
                  </div> : <Button variant="outline" size="sm" onClick={() => setShowCustomSpecInput(true)} icon={<Plus className="w-3.5 h-3.5" data-id="element-13941" />} data-id="element-13940">
                    Своя
                  </Button>}
              </div>
            </div>

            {/* About */}
            <div data-id="element-13942">
              <div className="flex justify-between items-center mb-1" data-id="element-13943">
                <label className="text-xs font-medium text-slate-600" data-id="element-13944">
                  О себе
                </label>
                <button onClick={() => setShowAboutHint(!showAboutHint)} className="text-[11px] text-indigo-600 hover:text-indigo-700 font-medium" data-id="element-13945">
                  {showAboutHint ? 'Скрыть' : 'Что писать?'}
                </button>
              </div>
              <p className="text-[11px] text-indigo-600 mb-2 flex items-center gap-1" data-id="element-13946">
                <MapPin className="w-3 h-3" data-id="element-13947" /> Отображается: первый текстовый
                блок под шапкой
              </p>
              {showAboutHint && <div className="mb-2 p-2.5 bg-indigo-50 border border-indigo-100 rounded-lg text-xs text-indigo-700 space-y-1" data-id="element-13948">
                  <p data-id="element-13949">
                    Расскажите о себе: опыт, подход, чем отличаетесь. Клиенты
                    выбирают тех, кому доверяют.
                  </p>
                  <p className="text-indigo-500 italic" data-id="element-13950">
                    Пример: «Привет! Я Елена, астролог с 8-летним стажем. Мой
                    подход — перевести язык звёзд на понятный человеческий.»
                  </p>
                </div>}
              <textarea className="w-full rounded-lg border-slate-200 px-3 py-2.5 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm resize-none" rows={3} value={aboutText} onChange={e => setAboutText(e.target.value.slice(0, MAX_ABOUT_LENGTH))} onFocus={() => setHighlightedBlock('about')} onBlur={() => setHighlightedBlock(null)} data-id="element-13951" />
              <div className="flex justify-end mt-0.5" data-id="element-13952">
                <span className={`text-[11px] ${aboutText.length > 900 ? 'text-rose-500' : 'text-slate-400'}`} data-id="element-13953">
                  {aboutText.length}/{MAX_ABOUT_LENGTH}
                </span>
              </div>
            </div>
          </Card>

          {/* Consultation Settings */}
          <Card padding="md" data-id="element-13954">
            <SectionHeader title="Запись на консультацию" section="consultation" badge={<Badge variant="success" size="sm" data-id="element-13956">
                  Ключевой блок
                </Badge>} data-id="element-13955" />
            <SectionBody section="consultation" data-id="element-13957">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-13958">
                <MapPin className="w-3 h-3" data-id="element-13959" /> Отображается: боковая панель
                справа (кнопка записи)
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3" data-id="element-13960">
                <div data-id="element-13961">
                  <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-13962">
                    Цена
                  </label>
                  <div className="flex gap-2" data-id="element-13963">
                    <select value={consultPriceType} onChange={e => setConsultPriceType(e.target.value as 'fixed' | 'from')} className="rounded-lg border-slate-200 px-2 py-2 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none" data-id="element-13964">
                      <option value="from" data-id="element-13965">от</option>
                      <option value="fixed" data-id="element-13966">фикс</option>
                    </select>
                    <input type="number" value={consultPrice} onChange={e => setConsultPrice(e.target.value)} className="flex-1 rounded-lg border-slate-200 px-3 py-2 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="5000" data-id="element-13967" />
                    <span className="flex items-center text-sm text-slate-500" data-id="element-13968">
                      ₽
                    </span>
                  </div>
                </div>
                <div data-id="element-13969">
                  <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-13970">
                    Длительность (мин)
                  </label>
                  <input type="number" value={consultDuration} onChange={e => setConsultDuration(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none" data-id="element-13971" />
                </div>
              </div>
              <div className="mb-3" data-id="element-13972">
                <label className="block text-xs font-medium text-slate-600 mb-2" data-id="element-13973">
                  Формат
                </label>
                <div className="flex gap-2" data-id="element-13974">
                  {[{
                  key: 'video' as const,
                  icon: Video,
                  label: 'Видео'
                }, {
                  key: 'chat' as const,
                  icon: MessageSquare,
                  label: 'Чат'
                }, {
                  key: 'audio' as const,
                  icon: Mic,
                  label: 'Аудио'
                }].map(f => <button key={f.key} onClick={() => setConsultFormats(p => ({
                  ...p,
                  [f.key]: !p[f.key]
                }))} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${consultFormats[f.key] ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`} data-id="element-13975">
                      <f.icon className="w-4 h-4" data-id="element-13976" /> {f.label}
                    </button>)}
                </div>
              </div>
              <div data-id="element-13977">
                <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-13978">
                  Текст кнопки
                </label>
                <input type="text" value={consultButtonText} onChange={e => setConsultButtonText(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none" data-id="element-13979" />
              </div>
            </SectionBody>
          </Card>

          {/* Contacts */}
          <Card padding="md" data-id="element-13980">
            <SectionHeader title="Контакты и кнопки связи" section="contacts" data-id="element-13981" />
            <SectionBody section="contacts" data-id="element-13982">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-13983">
                <MapPin className="w-3 h-3" data-id="element-13984" /> Отображается: блок «Связаться» на
                странице
              </p>
              <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg mb-3" data-id="element-13985">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" data-id="element-13986" />
                <p className="text-xs text-amber-700" data-id="element-13987">
                  Не уводите клиентов с платформы. Заказы через AstroGuide —
                  безопасность для вас и клиентов.
                </p>
              </div>
              <div className="flex gap-3 mb-4" data-id="element-13988">
                <button onClick={() => setShowChatButton(!showChatButton)} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium ${showChatButton ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500'}`} data-id="element-13989">
                  {showChatButton ? <ToggleRight className="w-5 h-5 text-emerald-500" data-id="element-13990" /> : <ToggleLeft className="w-5 h-5 text-slate-300" data-id="element-13991" />}{' '}
                  Кнопка «Написать»
                </button>
                <button onClick={() => setShowBookButton(!showBookButton)} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium ${showBookButton ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500'}`} data-id="element-13992">
                  {showBookButton ? <ToggleRight className="w-5 h-5 text-emerald-500" data-id="element-13993" /> : <ToggleLeft className="w-5 h-5 text-slate-300" data-id="element-13994" />}{' '}
                  Кнопка «Записаться»
                </button>
              </div>
              <div className="space-y-2" data-id="element-13995">
                {contacts.map((c, idx) => <div key={c.id} className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition-colors ${c.enabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-50'}`} data-id="element-13996">
                    <button onClick={() => setContacts(prev => prev.map((ct, i) => i === idx ? {
                  ...ct,
                  enabled: !ct.enabled
                } : ct))} className="shrink-0" data-id="element-13997">
                      {c.enabled ? <ToggleRight className="w-5 h-5 text-emerald-500" data-id="element-13998" /> : <ToggleLeft className="w-5 h-5 text-slate-300" data-id="element-13999" />}
                    </button>
                    <span className={`w-6 h-6 rounded flex items-center justify-center text-[9px] font-bold shrink-0 ${c.color}`} data-id="element-14000">
                      {c.code}
                    </span>
                    <input type="text" value={c.value} onChange={e => setContacts(prev => prev.map((ct, i) => i === idx ? {
                  ...ct,
                  value: e.target.value
                } : ct))} placeholder={c.label} disabled={!c.enabled} className="flex-1 rounded-lg border-slate-200 px-2.5 py-1.5 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-400 min-w-0" data-id="element-14001" />
                  </div>)}
              </div>
            </SectionBody>
          </Card>

          {/* Stats Block */}
          <Card padding="md" data-id="element-14002">
            <SectionHeader title="Блок статистики" section="stats" data-id="element-14003" />
            <SectionBody section="stats" data-id="element-14004">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-14005">
                <MapPin className="w-3 h-3" data-id="element-14006" /> Отображается: под шапкой (1250+,
                94%, 15 мин)
              </p>
              <div className="flex items-center gap-3 mb-3" data-id="element-14007">
                <button onClick={() => setShowStats(!showStats)} className="shrink-0" data-id="element-14008">
                  {showStats ? <ToggleRight className="w-6 h-6 text-emerald-500" data-id="element-14009" /> : <ToggleLeft className="w-6 h-6 text-slate-300" data-id="element-14010" />}
                </button>
                <span className="text-sm font-medium text-slate-700" data-id="element-14011">
                  {showStats ? 'Блок включён' : 'Блок выключен'}
                </span>
              </div>
              {showStats && <div className="grid grid-cols-3 gap-3" data-id="element-14012">
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-center" data-id="element-14013">
                    <div className="text-lg font-bold text-slate-900" data-id="element-14014">
                      {statsData.consultations}+
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-14015">
                      Консультаций (авто)
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-center" data-id="element-14016">
                    <div className="text-lg font-bold text-slate-900" data-id="element-14017">
                      {statsData.rating}%
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-14018">
                      Довольны (авто)
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-center" data-id="element-14019">
                    <div className="text-lg font-bold text-slate-900" data-id="element-14020">
                      {statsData.responseTime} мин
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-14021">
                      Время ответа (авто)
                    </div>
                  </div>
                </div>}
            </SectionBody>
          </Card>

          {/* Services */}
          <Card padding="md" data-id="element-14022">
            <SectionHeader title="Услуги" section="services" data-id="element-14023" />
            <SectionBody section="services" data-id="element-14024">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-14025">
                <MapPin className="w-3 h-3" data-id="element-14026" /> Отображается: блок «Услуги» на
                странице
              </p>
              <div className="space-y-2 mb-3" data-id="element-14027">
                {services.map((srv, idx) => <div key={srv.id} className={`flex items-center gap-2.5 p-3 rounded-lg border ${srv.isTop ? 'border-amber-300 bg-amber-50/30' : 'border-slate-100 bg-slate-50/50'}`} onMouseEnter={() => setHighlightedBlock('services')} onMouseLeave={() => setHighlightedBlock(null)} data-id="element-14028">
                    <div className="flex flex-col gap-0.5 shrink-0" data-id="element-14029">
                      <button onClick={() => {
                    const n = [...services];
                    if (idx > 0) {
                      ;
                      [n[idx], n[idx - 1]] = [n[idx - 1], n[idx]];
                      setServices(n);
                    }
                  }} disabled={idx === 0} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30" data-id="element-14030">
                        <ChevronUp className="w-3.5 h-3.5" data-id="element-14031" />
                      </button>
                      <button onClick={() => {
                    const n = [...services];
                    if (idx < n.length - 1) {
                      ;
                      [n[idx], n[idx + 1]] = [n[idx + 1], n[idx]];
                      setServices(n);
                    }
                  }} disabled={idx === services.length - 1} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30" data-id="element-14032">
                        <ChevronDown className="w-3.5 h-3.5" data-id="element-14033" />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-14034">
                      <div className="font-medium text-slate-900 text-sm flex items-center gap-1.5" data-id="element-14035">
                        {srv.isTop && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" data-id="element-14036" />}
                        {srv.name}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-14037">
                        {srv.price} · {srv.duration}
                      </div>
                    </div>
                    <button onClick={() => setServices(p => p.map(s => s.id === srv.id ? {
                  ...s,
                  isTop: !s.isTop
                } : s))} className={`p-1.5 rounded-lg transition-colors ${srv.isTop ? 'text-amber-500 bg-amber-100' : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50'}`} title="Выделить как топ" data-id="element-14038">
                      <Star className="w-4 h-4" data-id="element-14039" />
                    </button>
                    <Button variant="ghost" size="sm" className="text-indigo-600 shrink-0" onClick={() => showToast(`Редактирование: ${srv.name}`)} data-id="element-14040">
                      Изменить
                    </Button>
                  </div>)}
              </div>
              <Button variant="primary" size="sm" icon={<Plus className="w-3.5 h-3.5" data-id="element-14042" />} onClick={() => showToast('Создание услуги')} data-id="element-14041">
                Добавить услугу
              </Button>
            </SectionBody>
          </Card>

          {/* Reviews Settings */}
          <Card padding="md" data-id="element-14043">
            <SectionHeader title="Отзывы" section="reviews" data-id="element-14044" />
            <SectionBody section="reviews" data-id="element-14045">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-14046">
                <MapPin className="w-3 h-3" data-id="element-14047" /> Отображается: блок «Отзывы
                клиентов»
              </p>
              <div className="flex gap-2 mb-3" data-id="element-14048">
                {(['all', 'best'] as const).map(m => <button key={m} onClick={() => setReviewsMode(m)} className={`px-3 py-2 rounded-lg border text-sm font-medium ${reviewsMode === m ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500'}`} data-id="element-14049">
                    {m === 'all' ? 'Все отзывы' : 'Лучшие (4-5 ⭐)'}
                  </button>)}
              </div>
              <p className="text-xs text-slate-500" data-id="element-14050">
                Закреплённых отзывов: {pinnedReviews.length}. Управление
                отзывами → вкладка «Отзывы».
              </p>
            </SectionBody>
          </Card>

          {/* Products Settings */}
          <Card padding="md" data-id="element-14051">
            <SectionHeader title="Товары / Курсы" section="products" data-id="element-14052" />
            <SectionBody section="products" data-id="element-14053">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-14054">
                <MapPin className="w-3 h-3" data-id="element-14055" /> Отображается: блок «Товары» после
                услуг
              </p>
              <div className="flex items-center gap-3 mb-3" data-id="element-14056">
                <button onClick={() => setShowProducts(!showProducts)} data-id="element-14057">
                  {showProducts ? <ToggleRight className="w-6 h-6 text-emerald-500" data-id="element-14058" /> : <ToggleLeft className="w-6 h-6 text-slate-300" data-id="element-14059" />}
                </button>
                <span className="text-sm font-medium text-slate-700" data-id="element-14060">
                  {showProducts ? 'Блок включён' : 'Блок выключен'}
                </span>
              </div>
              {showProducts && <div data-id="element-14061">
                  <label className="block text-xs font-medium text-slate-600 mb-2" data-id="element-14062">
                    Показывать
                  </label>
                  <div className="flex gap-2" data-id="element-14063">
                    {([3, 6, 'all'] as const).map(n => <button key={String(n)} onClick={() => setProductsCount(n)} className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${productsCount === n ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500'}`} data-id="element-14064">
                        {n === 'all' ? 'Все' : `${n} шт`}
                      </button>)}
                  </div>
                </div>}
            </SectionBody>
          </Card>

          {/* Profile Constructor */}
          <Card padding="md" data-id="element-14065">
            <SectionHeader title="Конструктор профиля" section="constructor" data-id="element-14066" />
            <SectionBody section="constructor" data-id="element-14067">
              <div className="flex justify-between items-center mb-3" data-id="element-14068">
                <p className="text-xs text-slate-500" data-id="element-14069">
                  Порядок и видимость блоков на публичной странице
                </p>
                <Button variant="ghost" size="sm" className="text-indigo-600" icon={<Sparkles className="w-3.5 h-3.5" data-id="element-14071" />} onClick={applyRecommendedOrder} data-id="element-14070">
                  Рекомендуемый
                </Button>
              </div>
              <div className="space-y-1.5" data-id="element-14072">
                {blocks.map((block, idx) => <div key={block.id} className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition-colors ${block.enabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-50'}`} onMouseEnter={() => setHighlightedBlock(block.id)} onMouseLeave={() => setHighlightedBlock(null)} data-id="element-14073">
                    <div className="flex flex-col gap-0.5 shrink-0" data-id="element-14074">
                      <button onClick={() => moveBlock(idx, 'up')} disabled={idx === 0} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30" data-id="element-14075">
                        <ChevronUp className="w-3.5 h-3.5" data-id="element-14076" />
                      </button>
                      <button onClick={() => moveBlock(idx, 'down')} disabled={idx === blocks.length - 1} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30" data-id="element-14077">
                        <ChevronDown className="w-3.5 h-3.5" data-id="element-14078" />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-14079">
                      <span className="text-sm font-medium text-slate-900" data-id="element-14080">
                        {block.label}
                      </span>
                      <div className="text-[10px] text-slate-400" data-id="element-14081">
                        {block.location}
                      </div>
                    </div>
                    <button onClick={() => {
                  setHighlightedBlock(block.id);
                  setTimeout(() => setHighlightedBlock(null), 2000);
                }} className="p-1 text-slate-400 hover:text-indigo-600" title="Показать на превью" data-id="element-14082">
                      <Eye className="w-3.5 h-3.5" data-id="element-14083" />
                    </button>
                    <button onClick={() => setBlocks(prev => prev.map(b => b.id === block.id ? {
                  ...b,
                  enabled: !b.enabled
                } : b))} data-id="element-14084">
                      {block.enabled ? <ToggleRight className="w-5 h-5 text-emerald-500" data-id="element-14085" /> : <ToggleLeft className="w-5 h-5 text-slate-300" data-id="element-14086" />}
                    </button>
                  </div>)}
              </div>
            </SectionBody>
          </Card>

          {/* Verification */}
          <Card padding="md" className="border-indigo-100 bg-indigo-50/30" data-id="element-14087">
            <SectionHeader title="Верификация" section="verification" badge={verificationStatus === 'unverified' ? <Badge variant="warning" size="sm" data-id="element-14089">
                    Не пройдена
                  </Badge> : verificationStatus === 'pending' ? <Badge variant="pro" size="sm" data-id="element-14090">
                    На проверке
                  </Badge> : <Badge variant="success" size="sm" data-id="element-14091">
                    Пройдена
                  </Badge>} data-id="element-14088" />
            <SectionBody section="verification" data-id="element-14092">
              <p className="text-xs text-slate-600 mb-3" data-id="element-14093">
                Верифицированные профили получают значок PRO и больше доверия.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3" data-id="element-14094">
                {[{
                icon: FileCheck,
                title: 'Паспорт / ID'
              }, {
                icon: GraduationCap,
                title: 'Диплом'
              }, {
                icon: FileText,
                title: 'Доп. документы'
              }].map(doc => <div key={doc.title} className="border-2 border-dashed border-slate-300 rounded-lg p-3 text-center hover:bg-white cursor-pointer" onClick={() => showToast(`Загрузка: ${doc.title}`)} data-id="element-14095">
                    <doc.icon className="w-5 h-5 text-slate-400 mx-auto mb-1" data-id="element-14096" />
                    <div className="text-xs font-bold text-slate-900" data-id="element-14097">
                      {doc.title}
                    </div>
                    <span className="text-[10px] text-indigo-600 font-medium" data-id="element-14098">
                      Загрузить
                    </span>
                  </div>)}
              </div>
              <div className="flex justify-end" data-id="element-14099">
                <Button variant="primary" size="sm" disabled={verificationStatus !== 'unverified'} onClick={() => {
                setVerificationStatus('pending');
                showToast('Документы отправлены ✓');
              }} data-id="element-14100">
                  {verificationStatus === 'unverified' ? 'Отправить на проверку' : verificationStatus === 'pending' ? 'На проверке...' : 'Верифицирован ✓'}
                </Button>
              </div>
            </SectionBody>
          </Card>
        </div>

        {/* Preview Column */}
        {showPreview && <div className="lg:w-[360px] shrink-0 lg:sticky lg:top-4 lg:self-start" data-id="element-14101">
            <div className="flex items-center justify-between mb-3" data-id="element-14102">
              <span className="text-sm font-bold text-slate-900" data-id="element-14103">
                Предпросмотр
              </span>
              <div className="flex bg-slate-100 p-0.5 rounded-lg" data-id="element-14104">
                <button onClick={() => setPreviewDevice('desktop')} className={`p-1.5 rounded-md ${previewDevice === 'desktop' ? 'bg-white shadow-sm' : ''}`} data-id="element-14105">
                  <Monitor className="w-4 h-4 text-slate-600" data-id="element-14106" />
                </button>
                <button onClick={() => setPreviewDevice('mobile')} className={`p-1.5 rounded-md ${previewDevice === 'mobile' ? 'bg-white shadow-sm' : ''}`} data-id="element-14107">
                  <Smartphone className="w-4 h-4 text-slate-600" data-id="element-14108" />
                </button>
              </div>
            </div>
            <MiniPreview data-id="element-14109" />
          </div>}
      </div>

      {/* Mobile Sticky Save */}
      <div className="fixed bottom-0 left-0 right-0 p-3 bg-white border-t border-slate-200 sm:hidden z-40 shadow-lg" data-id="element-14110">
        <Button variant="primary" fullWidth icon={<Save className="w-4 h-4" data-id="element-14112" />} onClick={handleSave} data-id="element-14111">
          Сохранить
        </Button>
      </div>
    </div>;
}